import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Card,
  Row,
  Col,
  Form,
  Select,
  Input,
  Button,
  Space,
  Tag,
  Typography,
  message,
} from "antd";
import { ClockCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { FormInstance } from "antd";
import { formatPhone } from "../../../utils/phone";

import { apiClient } from "../../../services/api/apiClient";
import { ContactProposalModal } from "./ContactProposalModal";

const { Text } = Typography;

interface ContactReceiverSectionProps {
  audit: any;
  form?: FormInstance;
  /** Only used as a fallback list of positions when filling the
   *  read-only display of a selected contact. */
  positionSelectProps: any;
}

/** Same shape as `ContactRoleSerializer` in the backend. */
interface ChainEntry {
  id: string;
  producer_contact: string;
  // UnifiedContact.id is a UUID string.
  contact_id: string | null;
  contact_name: string;
  contact_email: string;
  contact_phone: string;
  contact_nif: string;
  position: number | null;
  position_name: string | null;
  target: "license" | "farm";
  farm: number | null;
  priority: number;
}

interface ChainsResponse {
  license: ChainEntry[];
  farms: { farm_id: number; farm_name: string; contacts: ChainEntry[] }[];
}

/** Same shape as the `PendingProposal` in `ContactChainsPanel`. */
interface PendingProposal {
  ticket_id: number;
  ticket_code: string;
  kind: "new" | "edit" | "remove";
  contact_id: string | null;
  contact_name: string | null;
  farm_id: number | null;
  proposed?: {
    name?: string;
    phone?: string;
    email?: string;
    position?: string;
  } | null;
}

const NEW_CONTACT_SENTINEL = "__NEW_CONTACT__";
/** Prefix used to namespace pending-proposal option values, so they
 *  don't collide with real UUID contact ids in the Select state. */
const PENDING_PREFIX = "pending:";

/**
 * Contact-receiver selector for the audit edit-info form.
 *
 * The list of choices comes from the producer's contact-chains
 * (`/producers/{id}/contact-chains/`) — the same data the audit
 * detail's Contactos panel renders — preferring the audit-farm chain
 * over the license chain.
 *
 * Picking a contact fills the receiver fields (read-only). Picking
 * "Nuevo contacto" opens the same `ContactProposalModal` (kind="new")
 * used in the audit detail, so adding a contact goes through the
 * standard admin-approval workflow.
 */
export const ContactReceiverSection: React.FC<ContactReceiverSectionProps> = ({
  audit,
  form,
}) => {
  const { t } = useTranslation();
  const [chains, setChains] = useState<ChainsResponse | null>(null);
  const [pendingProposals, setPendingProposals] = useState<PendingProposal[]>(
    [],
  );
  const [loading, setLoading] = useState(false);
  const [selectedContactId, setSelectedContactId] = useState<
    string | "__NEW_CONTACT__" | null
  >(null);
  const [proposalOpen, setProposalOpen] = useState(false);

  const producerId: number | null = audit?.farm?.producer?.id ?? null;
  const auditId: number | null = audit?.id ?? null;
  const auditFarmId: number | null = audit?.farm?.id ?? null;

  const fetchChains = useCallback(async () => {
    if (!producerId) return;
    setLoading(true);
    try {
      const res = await apiClient.get<ChainsResponse>(
        `/producers/${producerId}/contact-chains/`,
      );
      setChains(res.data);
    } catch {
      message.error(
        t(
          "audit.contact_chains_error",
          "No se pudo cargar los contactos del productor",
        ),
      );
      setChains({ license: [], farms: [] });
    } finally {
      setLoading(false);
    }
  }, [producerId, t]);

  // Pending `new` contact proposals raised against this audit. They
  // don't have a real `UnifiedContact` yet, but we still want them to
  // appear in the picker so the auditor can record who they're being
  // received by — even before the admin validates the proposal.
  const fetchPendingProposals = useCallback(async () => {
    if (!auditId) return;
    try {
      const res = await apiClient.get<PendingProposal[]>(
        `/audits/${auditId}/contact-proposals/`,
      );
      setPendingProposals(Array.isArray(res.data) ? res.data : []);
    } catch {
      setPendingProposals([]);
    }
  }, [auditId]);

  useEffect(() => {
    fetchChains();
    fetchPendingProposals();
  }, [fetchChains, fetchPendingProposals]);

  // Initialize selection from the saved audit value.
  useEffect(() => {
    if (!chains || selectedContactId !== null) return;
    const raw = audit?.contact_receiver_unified;
    const id = typeof raw === "object" && raw ? raw.id : raw;
    if (id) setSelectedContactId(id);
  }, [chains, audit?.contact_receiver_unified, selectedContactId]);

  // Pick the farm chain when available; fall back to license.
  const activeChain: ChainEntry[] = useMemo(() => {
    if (!chains) return [];
    if (auditFarmId != null) {
      const farmChain = chains.farms.find((f) => f.farm_id === auditFarmId);
      if (farmChain && farmChain.contacts.length > 0) return farmChain.contacts;
    }
    return chains.license;
  }, [chains, auditFarmId]);

  // Dedupe by contact_id so the same person doesn't appear twice when
  // they have roles in both the farm and the license chains.
  const options = useMemo(() => {
    const seen = new Set<string>();
    type OptionShape = {
      value: string;
      label: React.ReactNode;
      entry?: ChainEntry;
      pending?: PendingProposal;
    };
    const items: OptionShape[] = [];

    for (const e of activeChain) {
      if (e.contact_id == null || seen.has(e.contact_id)) continue;
      seen.add(e.contact_id);
      items.push({
        value: e.contact_id,
        entry: e,
        label: (
          <Space size={6}>
            <Tag color="default" style={{ minWidth: 26, textAlign: "center", margin: 0 }}>
              #{e.priority}
            </Tag>
            <strong>{e.contact_name || "—"}</strong>
            {e.position_name && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                · {e.position_name}
              </Text>
            )}
          </Space>
        ),
      });
    }

    // Append pending `new` proposals — same picker, distinct value
    // namespace so we know later that it's a proposal, not a real
    // contact. Only the audit-farm pending proposals are surfaced
    // (matching the behaviour of `ContactChainsPanel`).
    for (const p of pendingProposals) {
      if (p.kind !== "new") continue;
      if (auditFarmId != null && p.farm_id != null && p.farm_id !== auditFarmId) {
        continue;
      }
      items.push({
        value: `${PENDING_PREFIX}${p.ticket_id}`,
        pending: p,
        label: (
          <Space size={6}>
            <Tag color="gold" icon={<ClockCircleOutlined />} style={{ margin: 0 }}>
              {t("contacts.pending_new", "Alta pendiente")}
            </Tag>
            <strong>{p.proposed?.name || "—"}</strong>
            {p.proposed?.position && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                · {p.proposed.position}
              </Text>
            )}
          </Space>
        ),
      });
    }

    // Si el contacto guardado en la auditoría no aparece en la
    // cadena activa (caso típico: el receptor lo eligió el auditor
    // en campo desde la cadena de licencia pero la cadena activa es
    // la de finca, o el contacto fue desactivado posteriormente),
    // sintetizamos una opción con los datos del propio audit para
    // que el Select pueda mostrarlo con su nombre — si no, se
    // quedaría rendereando el UUID en bruto.
    const savedRaw = audit?.contact_receiver_unified;
    const savedId =
      savedRaw && typeof savedRaw === "object" ? savedRaw.id : savedRaw;
    if (savedId && !seen.has(savedId)) {
      const savedName =
        (savedRaw && typeof savedRaw === "object" && savedRaw.name)
        || audit?.contact_receiver
        || "—";
      const savedPositionName = audit?.position || null;
      items.push({
        value: savedId,
        label: (
          <Space size={6}>
            <Tag
              color="default"
              style={{ minWidth: 26, textAlign: "center", margin: 0 }}
            >
              ·
            </Tag>
            <strong>{savedName}</strong>
            {savedPositionName && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                · {savedPositionName}
              </Text>
            )}
          </Space>
        ),
      });
    }
    return items;
  }, [
    activeChain,
    pendingProposals,
    auditFarmId,
    audit?.contact_receiver_unified,
    audit?.contact_receiver,
    audit?.position,
    t,
  ]);

  const handleSelect = (value: string | "__NEW_CONTACT__" | null) => {
    if (value === NEW_CONTACT_SENTINEL) {
      // Don't lock the selection — just open the proposal modal.
      setProposalOpen(true);
      return;
    }
    setSelectedContactId(value);
    if (value === null) {
      form?.setFieldsValue({
        contact_receiver_unified: null,
        contact_receiver: "",
        contact_receiver_nif: "",
        contact_receiver_phone: "",
        position: "",
      });
      return;
    }

    // Pending "new" proposal: fill the free-text receiver fields with
    // the proposed values, but leave `contact_receiver_unified=null`
    // because the underlying `UnifiedContact` doesn't exist yet.
    if (value.startsWith(PENDING_PREFIX)) {
      const ticketId = Number(value.slice(PENDING_PREFIX.length));
      const proposal = pendingProposals.find((p) => p.ticket_id === ticketId);
      if (!proposal) return;
      form?.setFieldsValue({
        contact_receiver_unified: null,
        contact_receiver: proposal.proposed?.name ?? "",
        contact_receiver_nif: "",
        contact_receiver_phone: proposal.proposed?.phone ?? "",
        position: proposal.proposed?.position ?? "",
      });
      return;
    }

    const entry = activeChain.find((e) => e.contact_id === value);
    if (!entry) return;
    form?.setFieldsValue({
      contact_receiver_unified: entry.contact_id,
      contact_receiver: entry.contact_name,
      contact_receiver_nif: entry.contact_nif || "",
      contact_receiver_phone: entry.contact_phone || "",
      position: entry.position_name || "",
    });
  };

  const selectValue =
    selectedContactId === null || selectedContactId === NEW_CONTACT_SENTINEL
      ? undefined
      : selectedContactId;
  const showSelected =
    typeof selectedContactId === "string" &&
    selectedContactId !== NEW_CONTACT_SENTINEL;
  const isPendingSelected =
    typeof selectedContactId === "string" &&
    selectedContactId.startsWith(PENDING_PREFIX);

  return (
    <Card
      title={t("audit.contact_person", "Persona Encargada de Recibirnos")}
      style={{ marginBottom: 24 }}
      extra={
        producerId ? (
          <Button
            type="dashed"
            icon={<PlusOutlined />}
            onClick={() => setProposalOpen(true)}
          >
            {t("audit.propose_new_contact", "Proponer nuevo contacto")}
          </Button>
        ) : null
      }
    >
      <Form.Item label={t("audit.select_contact", "Seleccionar contacto")}>
        <Select
          placeholder={t(
            "audit.select_contact_placeholder",
            "Selecciona un contacto del productor",
          )}
          allowClear
          showSearch
          loading={loading}
          value={selectValue}
          onChange={(v) => handleSelect((v as string) ?? null)}
          optionLabelProp="label"
          filterOption={(input, option) => {
            const o = option as any;
            const entry = o?.entry as ChainEntry | undefined;
            const pending = o?.pending as PendingProposal | undefined;
            const haystack = entry
              ? [
                  entry.contact_name,
                  entry.position_name ?? "",
                  entry.contact_phone ?? "",
                  entry.contact_email ?? "",
                ].join(" ")
              : pending
                ? [
                    pending.proposed?.name ?? "",
                    pending.proposed?.position ?? "",
                    pending.proposed?.phone ?? "",
                    pending.proposed?.email ?? "",
                  ].join(" ")
                : "";
            return haystack.toLowerCase().includes(input.toLowerCase());
          }}
          options={options as any}
        />
        {options.length === 0 && !loading && (
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t(
              "audit.no_producer_contacts",
              "El productor no tiene contactos. Propón uno nuevo para enviarlo a validación.",
            )}
          </Text>
        )}
      </Form.Item>

      {isPendingSelected && (
        <Tag color="gold" icon={<ClockCircleOutlined />} style={{ marginBottom: 12 }}>
          {t(
            "audit.pending_contact_notice",
            "Contacto pendiente de validar — solo se guarda como texto libre",
          )}
        </Tag>
      )}

      {showSelected && (
        <Row gutter={16}>
          <Col xs={24} sm={12}>
            <Form.Item label={t("audit.contact_receiver", "Nombre")}>
              <Input value={form?.getFieldValue("contact_receiver")} disabled />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={t("audit.contact_receiver_nif", "DNI")}>
              <Input value={form?.getFieldValue("contact_receiver_nif")} disabled />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={t("audit.contact_receiver_phone", "Teléfono")}>
              <Input
                value={formatPhone(form?.getFieldValue("contact_receiver_phone"))}
                disabled
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12}>
            <Form.Item label={t("audit.position", "Cargo")}>
              <Input value={form?.getFieldValue("position")} disabled />
            </Form.Item>
          </Col>
        </Row>
      )}

      {/* Hidden field bound to the form value, kept in sync via setFieldsValue */}
      <Form.Item name="contact_receiver_unified" hidden>
        <Input />
      </Form.Item>
      {/* These are kept for backwards compatibility with the existing
          payload contract (`contact_receiver`, `contact_receiver_nif`,
          `contact_receiver_phone`, `position`). The Form holds them as
          hidden values; the visible inputs above show read-only previews
          when a contact is picked. */}
      <Form.Item name="contact_receiver" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="contact_receiver_nif" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="contact_receiver_phone" hidden>
        <Input />
      </Form.Item>
      <Form.Item name="position" hidden>
        <Input />
      </Form.Item>

      {producerId && audit?.id && (
        <ContactProposalModal
          visible={proposalOpen}
          kind="new"
          auditId={audit.id}
          auditReference={audit.reference ?? ""}
          producerId={producerId}
          producerName={audit.farm?.producer?.name ?? ""}
          farmId={auditFarmId}
          farmName={audit.farm?.name ?? null}
          contact={null}
          onClose={() => setProposalOpen(false)}
          onSubmitted={() => {
            // Two refetches: chain (in case the admin already
            // applied something) and pending proposals (so the new
            // ticket appears in the picker right away tagged as
            // "Alta pendiente").
            fetchChains();
            fetchPendingProposals();
          }}
        />
      )}
    </Card>
  );
};
